import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, MapPin, Home, Sparkles } from "lucide-react";
import { MAPS_URL, ADDRESS, CONTACT } from "../../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU = [
  { id: "residences", label: "View residences & pricing" },
  { id: "community", label: "Community & wellness spaces" },
  { id: "amenities", label: "Amenities & location" },
  { id: "visit", label: "Book a site visit" },
  { id: "contact", label: "Call or email us" },
  { id: "whatsapp", label: "Chat on WhatsApp" },
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quick, setQuick] = useState(MENU);
  const [stage, setStage] = useState("menu"); // menu | ask_name | ask_phone
  const [lead, setLead] = useState({ name: "", phone: "", intent: "" });
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      pushBot(
        "Namaste, and welcome to Sarrgosa — conscious luxury above the Kasauli hills. How may I help you today?",
      );
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" });
  }, [messages, typing]);

  const pushBot = (text, opts) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { from: "bot", text }]);
      if (opts !== undefined) setQuick(opts);
    }, 550);
  };
  const pushUser = (text) =>
    setMessages((m) => [...m, { from: "user", text }]);

  const submitLead = async (l) => {
    const intentLabel =
      l.intent === "visit"
        ? "Site visit request"
        : l.intent === "whatsapp"
          ? "WhatsApp callback request"
          : "Brochure & price list request";
    try {
      await axios.post(`${API}/enquiries`, {
        name: l.name,
        phone: l.phone,
        message: `[Concierge chat] ${intentLabel}`,
        source: "chatbot",
      });
      pushBot(
        `Thank you, ${l.name}. Our Sarrgosa relationship manager will reach you on ${l.phone} shortly. You can also reach us directly at ${CONTACT.phone} or ${CONTACT.email}. Anything else I can help with?`,
        MENU,
      );
    } catch (e) {
      pushBot(
        "Apologies — something went wrong saving your details. Please try again, or use the enquiry form below.",
        MENU,
      );
    }
    setStage("menu");
    setLead({ name: "", phone: "", intent: "" });
  };

  const startCapture = (intent, intro) => {
    setLead((l) => ({ ...l, intent }));
    if (intro) pushBot(intro);
    pushBot("May I have your name, please?", []);
    setStage("ask_name");
  };

  const handleChoice = (id) => {
    const choice = MENU.find((m) => m.id === id) || quick.find((q) => q.id === id);
    if (choice) pushUser(choice.label);

    if (id === "residences") {
      pushBot(
        "Sarrgosa offers two thoughtfully designed homes: a Studio Apartment (750 sq.ft.) and a 1 BHK Hill Residence (1,000 sq.ft.), set across an exclusive 8-acre gated community of 240 residences. Shall I send you the brochure & price list?",
        [
          { id: "brochure", label: "Yes, send the brochure" },
          { id: "visit", label: "Book a site visit" },
          { id: "back", label: "Back to menu" },
        ],
      );
    } else if (id === "community") {
      pushBot(
        "What truly sets Sarrgosa apart is its conscious, happy community — four signature spaces:\n\n• The Healing Studio, for sound baths & meditation\n• The Brainstorm Studio, for ideas & workshops\n• The Co-Working Lounge, a mountain office with high-speed Wi-Fi\n• A Live Global Beverage Counter serving fresh coffee & world teas.\n\nWould you like to experience them on a site visit?",
        [
          { id: "visit", label: "Book a site visit" },
          { id: "back", label: "Back to menu" },
        ],
      );
    } else if (id === "contact") {
      pushBot(
        `You can reach the Sarrgosa sales team directly:\n\n• Phone: ${CONTACT.phone}\n• Email: ${CONTACT.email}\n• Instagram: ${CONTACT.instagramHandle}\n\nWe're happy to help. Anything else?`,
        [
          { id: "visit", label: "Book a site visit" },
          { id: "back", label: "Back to menu" },
        ],
      );
    } else if (id === "amenities") {
      pushBot(
        `With 40+ curated amenities — an infinity-edge pool, alpine wellness spa, clubhouse, forest trails and more — Sarrgosa sits at ${ADDRESS}. Would you like directions or a private site visit?`,
        [
          { id: "directions", label: "Get directions" },
          { id: "visit", label: "Book a site visit" },
          { id: "back", label: "Back to menu" },
        ],
      );
    } else if (id === "directions") {
      window.open(MAPS_URL, "_blank", "noopener");
      pushBot("Opening the location on Google Maps for you. Anything else?", MENU);
    } else if (id === "brochure") {
      startCapture(
        "brochure",
        "Wonderful. I'll arrange the brochure and price list.",
      );
    } else if (id === "visit") {
      startCapture(
        "visit",
        "Delighted to arrange that. A site visit is the best way to feel the Sarrgosa air.",
      );
    } else if (id === "whatsapp") {
      startCapture(
        "whatsapp",
        "Our dedicated WhatsApp line is being set up. Leave your number and we'll message you there as soon as it's live.",
      );
    } else if (id === "back") {
      pushBot("Of course. How else may I help?", MENU);
      setStage("menu");
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    pushUser(text);
    setInput("");

    if (stage === "ask_name") {
      const name = text;
      setLead((l) => ({ ...l, name }));
      setStage("ask_phone");
      pushBot(`Thank you, ${name}. What's the best phone number to reach you?`, []);
    } else if (stage === "ask_phone") {
      const phone = text;
      const finalLead = { ...lead, phone };
      setLead(finalLead);
      submitLead(finalLead);
    } else {
      pushBot(
        "I can help with residences, pricing, amenities, or booking a visit. Please pick an option below.",
        MENU,
      );
    }
  };

  return (
    <>
      {/* Toggle button */}
      <motion.button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, type: "spring", stiffness: 200, damping: 18 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open concierge chat"
        className="fixed bottom-6 right-6 z-[70] w-16 h-16 rounded-full btn-gold flex items-center justify-center shadow-[0_10px_40px_-8px_rgba(212,175,55,0.6)]"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-28 right-6 z-[70] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[75vh] flex flex-col rounded-2xl overflow-hidden gold-border bg-[#0b0b0b] shadow-2xl"
          >
            {/* Header */}
            <div className="glass border-b border-[#d4af37]/20 px-5 py-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
                <Sparkles size={18} className="text-[#d4af37]" />
              </span>
              <div>
                <p className="font-serif-lux text-lg text-[#f5f5f0] leading-none">
                  Sarrgosa Concierge
                </p>
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#d4af37] mt-1 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  Online now
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid="chatbot-message"
                  className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl whitespace-pre-line ${
                      m.from === "user"
                        ? "bg-[#d4af37] text-[#0a0a0a] rounded-br-sm"
                        : "bg-[#161616] text-[#e8e6df] border border-[#d4af37]/12 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}

              {typing && (
                <div className="flex justify-start">
                  <div className="bg-[#161616] border border-[#d4af37]/12 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        className="w-1.5 h-1.5 rounded-full bg-[#d4af37]"
                        animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                        transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Quick replies */}
              {!typing && quick.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {quick.map((q) => (
                    <button
                      key={q.id}
                      data-testid={`chatbot-option-${q.id}`}
                      onClick={() => handleChoice(q.id)}
                      className="text-xs tracking-wide rounded-full border border-[#d4af37]/35 text-[#e8e6df] px-3.5 py-2 hover:bg-[#d4af37] hover:text-[#0a0a0a] transition-colors duration-300 flex items-center gap-1.5"
                    >
                      {q.id === "directions" && <MapPin size={12} />}
                      {q.id === "residences" && <Home size={12} />}
                      {q.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#d4af37]/15 p-3 flex items-center gap-2 bg-[#0b0b0b]">
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder={
                  stage === "ask_phone"
                    ? "Type your phone number..."
                    : stage === "ask_name"
                      ? "Type your name..."
                      : "Type a message..."
                }
                className="flex-1 bg-black/40 border border-[#d4af37]/20 rounded-full px-4 py-2.5 text-sm text-[#f5f5f0] placeholder:text-[#6b6b64] focus:outline-none focus:border-[#d4af37]/60"
              />
              <button
                data-testid="chatbot-send"
                onClick={handleSend}
                aria-label="Send message"
                className="w-10 h-10 rounded-full btn-gold flex items-center justify-center shrink-0"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
